/*******************************************************************************
 *** Login ** 로그인
 *******************************************************************************/
import React from 'react';
import { useNavigate } from 'react-router-dom';
import JSEncrypt from 'jsencrypt';
import Util from '@/js/common/Util';
import Axios from '@/js/common/Axios';
import AppContext from '@/js/common/AppContext';
import { useSetRecoilState } from "recoil";
import * as Atom from "@/js/common/Atom";
import TextBox from '@/js/common/ui/input/TextBox';
import LabelCheckBox from '../common/ui/input/LabelCheckBox';

function Login() {
    const navigate = useNavigate();
    const setUserInfo = useSetRecoilState(Atom.userInfo);

    const [userId, setUserId] = React.useState<string>("");
    const [password, setPassword] = React.useState<string>("");
    const [saveUserId, setSaveUserId] = React.useState<boolean>(false); // 아이디 저장 체크
    const [autoLogin, setAutoLogin] = React.useState<"Y" | "N">("N");   // 자동로그인 체크

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.code === "Enter") {
            goLogin();
        }
    };

    // login 버튼 클릭 이벤트
    const goLogin = (id?: string, pw?: string) => {
        let finalId = userId;
        let finalPw = password;

        if (Util.isEmpty(finalId)) {
            finalId = id || "";
        }
        if (Util.isEmpty(finalPw)) {
            finalPw = pw || "";
        }

        if (Util.isEmpty(finalId)) {
            AppContext.openAlert({
                title: "아이디 확인",
                msg: "아이디를 입력해주세요.",
                icon: "warning"
            });
            return;
        }

        if (Util.isEmpty(finalPw)) {
            AppContext.openAlert({
                title: "비밀번호 확인",
                msg: "비밀번호를 입력해주세요.",
                icon: "warning"
            });
            return;
        }

        if (finalPw.length < 8) {
            AppContext.openAlert({
                title: "비밀번호 자릿수 확인",
                msg: "비밀번호는 8자리 이상이어야 합니다.",
                icon: "check"
            });
            return;
        }

        AppContext.showSpinner();

        Axios.dataAccess({
            url: "portal/auth/genRsaKeyByUserId.do",
            methodType: "post",
            paramType: "object",
            paramData: {
                userId: finalId
            },
            onSuccessFn: (res1: any) => {
                if (res1.item.loginRslt !== "O") {
                    AppContext.hideSpinner();
                    AppContext.openAlert({
                        title: "로그인 실패",
                        msg: res1.item.blockMsg,
                        icon: "warning"
                    });
                    return;
                }

                const pubKey = res1.item.pubKeyStr;
                const encrypt = new JSEncrypt();
                encrypt.setPublicKey(pubKey);
                const encPassword = encrypt.encrypt(finalPw);

                Axios.dataAccess({
                    url: "portal/auth/login.do",
                    methodType: "post",
                    paramType: "json",
                    paramData: {
                        userId: finalId,
                        encPassword: encPassword
                    },
                    onSuccessFn: (res2: any) => {
                        if (res2.item.loginRslt !== "O") {
                            AppContext.hideSpinner();
                            AppContext.openAlert({
                                title: "로그인 실패",
                                msg: res2.item.blockMsg,
                                icon: "warning"
                            });
                            return;
                        }

                        setUserInfo(res2.item.userInfo);

                        const tokenCrtDt = Util.getSpecificDate("-", "day", 0, "SS", "");
                        if (tokenCrtDt !== undefined) {
                            localStorage.setItem("tokenCrtDt", tokenCrtDt);
                        }

                        localStorage.setItem("grantType", res2.item.grantType);
                        localStorage.setItem("accessToken", res2.item.accessToken);
                        localStorage.setItem("refreshToken", res2.item.refreshToken);

                        if (saveUserId) {
                            localStorage.setItem("loginUserId", finalId);
                        } else {
                            localStorage.removeItem("loginUserId");
                        }

                        localStorage.setItem("autoLogin", autoLogin);
                        navigate('/portal/index');
                        AppContext.hideSpinner();
                    }
                });
            }
        });
    };

    // 아이디 저장 체크
    React.useEffect(() => {
        const loginUserId = localStorage.getItem("loginUserId");
        if (loginUserId === null) {
            setSaveUserId(false);
            return;
        }

        setSaveUserId(true);
        setUserId(loginUserId);

        const savedAutoLogin = localStorage.getItem("autoLogin");
        if (savedAutoLogin === "Y") {
            setAutoLogin("Y");
        }
    }, []);

    return (
        <div
            className='w100p dpFlx flxdCol aiC jcC'
            style={{
                minHeight: "100dvh",
                padding: "20px 16px",
                backgroundColor: "#f6f7f9",
                boxSizing: "border-box"
            }}
        >
            <div
                className="bgcWhite bdrd10 bsOut bsBdb"
                style={{
                    width: "min(500px, 100%)",
                    boxSizing: "border-box",
                    borderRadius: 16,
                    overflow: "hidden"
                }}
            >
                {/* 로고 */}
                <div
                    className='w100p dpFlx aiC jcC'
                    style={{
                        paddingTop: "clamp(28px, 5vw, 40px)"
                    }}
                >
                    <img
                        src={Util.getImgSrc("common/logo.png")}
                        style={{
                            width: "clamp(170px, 42vw, 238px)",
                            height: "auto",
                            display: "block"
                        }}
                        alt="logo"
                    />
                </div>

                {/* 제목 */}
                <div
                    className='w100p dpFlx aiC jcC'
                    style={{
                        paddingTop: "clamp(24px, 4vw, 40px)",
                        paddingBottom: "clamp(24px, 4vw, 40px)"
                    }}
                >
                    <span
                        className="fcBlack fw700"
                        style={{
                            fontSize: "clamp(28px, 6vw, 40px)",
                            letterSpacing: "-1px",
                            lineHeight: 1.2
                        }}
                    >
                        로그인
                    </span>
                </div>

                {/* 폼 영역 */}
                <div
                    className='w100p dpFlx aiC jcC'
                    style={{
                        padding: "0 20px 36px 20px",
                        boxSizing: "border-box"
                    }}
                >
                    <div
                        className='w100p'
                        style={{
                            maxWidth: 360
                        }}
                    >
                        <div className='h45 dpFlx aiFe pb5 bsBdb'>
                            <span className="SpoqaHanSansNeo-Bold fs16 ls08 fc222222">아이디</span>
                        </div>

                        <form onSubmit={(e) => e.preventDefault()}>
                            <TextBox value={userId}
                                type="text" height={56}
                                fontSize={16} fontFamily='SpoqaHanSansNeo-Regular'
                                maxLength={30} placeholder="아이디"
                                onChangeFunc={(text: string) => setUserId(text)}
                                inputClassName='bgcWhite ls08' color="#222222"
                                offColor="#999999" className='pl15 pr20'
                                borderColor='#999' borderOnColor='#222' borderRadius={10}
                                iconEl={
                                    <img
                                        onClick={() => setUserId("")}
                                        className={'csP ' + (Util.isEmpty(userId) ? "dpNone" : "")}
                                        src={Util.getImgSrc("common/icon_cancel_fill_18.png")}
                                        alt="icon_cancel_fill_18"
                                    />
                                }
                            />

                            <div className='h45 dpFlx aiFe pb5 bsBdb' style={{ marginTop: 8 }}>
                                <span className="SpoqaHanSansNeo-Bold fs16 ls08 fc222222">비밀번호</span>
                            </div>

                            <TextBox value={password}
                                type="password" height={56}
                                fontSize={16} fontFamily='Inter-Regular'
                                maxLength={30} placeholder="비밀번호"
                                onChangeFunc={(text: string) => setPassword(text)}
                                onKeyDown={handleKeyDown}
                                inputClassName='bgcWhite ls08' color="#222222"
                                offColor="#999999" className='pl15 pr20'
                                borderColor='#999' borderOnColor='#222' borderRadius={10}
                                iconEl={
                                    <img
                                        onClick={() => setPassword("")}
                                        className={'csP ' + (Util.isEmpty(password) ? "dpNone" : "")}
                                        src={Util.getImgSrc("common/icon_cancel_fill_18.png")}
                                        alt="icon_cancel_fill_18"
                                    />
                                }
                            />
                        </form>

                        {/* 체크박스 */}
                        <div className='dpFlx mt12 mb50'>
                            <LabelCheckBox checked={saveUserId}
                                onChange={() => setSaveUserId(!saveUserId)}
                                buttonColor='#000' labelText='아이디 저장' borderColor='#929191'
                                borderOnColor='#454545' color='#666'
                                textClassName='Pretendard-SemiBold fs15 ls08 ml5'
                            />
                        </div>

                        {/* 로그인 버튼 */}
                        <div className='dpFlx jcC aiC'>
                            <button
                                type="button"
                                className='w100p dpFlx aiC jcC csP'
                                style={{
                                    height: 56,
                                    backgroundColor: AppContext.themeColor,
                                    borderRadius: 10,
                                    border: "none",
                                    outline: "none"
                                }}
                                onClick={() => goLogin()}
                            >
                                <span className="Pretendard-Bold fs20 ls08 fcWhite">로그인</span>
                            </button>
                        </div>

                        {/* 하단 링크 */}
                        <div
                            className='w100p dpFlx aiC jcC mt30 mb30'
                            style={{
                                flexWrap: "wrap",
                                gap: "8px 12px",
                                textAlign: "center"
                            }}
                        >
                            <span
                                onClick={() => navigate('/portal/findUser')}
                                className="Pretendard-Regular fs13 ls065 fc666666 csP"
                            >
                                아이디 찾기
                            </span>

                            <span className="Pretendard-Regular fs13 fc999999">|</span>

                            <span
                                onClick={() => navigate('/portal/findPasswd')}
                                className="Pretendard-Regular fs13 ls065 fc666666 csP"
                            >
                                비밀번호 찾기
                            </span>

                            <span className="Pretendard-Regular fs13 fc999999">|</span>

                            <span
                                onClick={() => navigate('/portal/MbrJoinAply')}
                                className="Pretendard-Regular fs13 ls065 fc666666 csP"
                            >
                                회원 가입하기
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Login;